import React, { useEffect, useMemo, useRef, useState } from "react";
import CertificateCanvas from "../certificatePrint/CertificateCanvas";
import { getCssAtPageSize } from "../certificatePrint/certificateTypes";
import { resolveCertificateLogo } from "../certificatePrint/getLoggedInClinic";
import "../certificatePrint/certificatePrint.css";
import { buildNewBillPrintData } from "./buildNewBillPrintData";
import {
  fetchLatestNewBillStoreRaw,
  findNewBillTemplate,
  hydrateNewBillStore,
} from "./newBillStorage";
import { CertificateDesignerStore, CertificateTemplate } from "./newBillTypes";

export type NewBillPrintRuntimeProps = {
  storeRaw?: unknown;
  paymentResponse: any;
  paperRef?: React.RefObject<HTMLDivElement>;
};

function NewBillPrintRuntime({
  storeRaw,
  paymentResponse,
  paperRef: externalPaperRef,
}: NewBillPrintRuntimeProps) {
  const internalRef = useRef<HTMLDivElement>(null);
  const paperRef = externalPaperRef || internalRef;
  const [liveStoreRaw, setLiveStoreRaw] = useState<unknown>(storeRaw);

  useEffect(() => {
    setLiveStoreRaw(storeRaw);
  }, [storeRaw]);

  // Always prefer the clinic-saved template so Half Letter / layout edits show on Invoice.
  useEffect(() => {
    let cancelled = false;
    fetchLatestNewBillStoreRaw(storeRaw).then((saved) => {
      if (!cancelled && saved) setLiveStoreRaw(saved);
    });
    const onRefresh = (event: Event) => {
      const detail = (event as CustomEvent<{ store?: unknown }>).detail;
      if (detail?.store) setLiveStoreRaw(detail.store);
      else {
        fetchLatestNewBillStoreRaw(storeRaw).then((saved) => {
          if (saved) setLiveStoreRaw(saved);
        });
      }
    };
    window.addEventListener("newBillPrintRefresh", onRefresh);
    return () => {
      cancelled = true;
      window.removeEventListener("newBillPrintRefresh", onRefresh);
    };
  }, [storeRaw]);

  const store: CertificateDesignerStore = useMemo(
    () => hydrateNewBillStore(liveStoreRaw ?? storeRaw),
    [liveStoreRaw, storeRaw]
  );

  const template: CertificateTemplate = useMemo(
    () => findNewBillTemplate(store.templates, store.defaultId || store.activeId),
    [store]
  );

  const { previewData, previewTables } = useMemo(
    () =>
      buildNewBillPrintData({
        patient: paymentResponse?.patientData,
        clinic: paymentResponse?.amounts?.clinicId,
        doctor: paymentResponse?.amounts?.doctorId
          ? {
              firstName: paymentResponse.amounts.doctorId.firstName,
              lastName: paymentResponse.amounts.doctorId.lastName,
              title: paymentResponse.amounts.doctorId.title,
              designation: paymentResponse.amounts.doctorId.designation,
            }
          : undefined,
        amounts: paymentResponse?.amounts,
        treatments:
          paymentResponse?.treatmentdata?.filter(
            (item: any) => item?.draft !== true && item?.status !== "long term"
          ) ?? [],
        subTreatments:
          paymentResponse?.subPayments?.filter(
            (sub: any) => sub?.draft !== true && sub?.status !== "long term"
          ) ?? [],
        medicine: paymentResponse?.medicine ?? null,
      }),
    [paymentResponse]
  );

  const previewPage = useMemo(
    () => ({
      ...template.page,
      logoSrc: resolveCertificateLogo(
        paymentResponse?.amounts?.clinicId?.printImages,
        template.page.logoSrc
      ),
      letterheadSrc: template.page.letterheadSrc || "",
    }),
    [template.page, paymentResponse?.amounts?.clinicId?.printImages]
  );

  return (
    <div
      className="certificate-designer"
      data-new-bill-size={template.page.size}
      style={{ background: "transparent", padding: 0 }}
    >
      <style>
        {`@media print { @page { size: ${getCssAtPageSize(template.page.orientation, template.page.size)}; margin: 0; } }`}
      </style>
      <CertificateCanvas
        page={previewPage}
        elements={template.elements}
        selectedId={null}
        previewData={previewData}
        previewTables={previewTables}
        interactive={false}
        zoom={1}
        paperRef={paperRef}
      />
    </div>
  );
}

export default NewBillPrintRuntime;
